/**
 * Server functions for the OAuth consent UI. They resolve the session with
 * `auth.api.getSession` rather than the org-aware auth middleware, since the
 * consent flow itself is what selects the org.
 */
import { auth } from '@mesa/core/auth/better-auth';
import { getOAuthClientById } from '@mesa/core/auth/oauth/client-query';
import { type ConsentRedirect, approveConsent, rejectConsent } from '@mesa/core/auth/oauth/consent-server-fn';
import { type Scope, parseScopeString } from '@mesa/core/auth/types';
import { type OrgMembership, listOrgsForUser } from '@mesa/core/org/queries';
import { createServerFn } from '@tanstack/react-start';
import { getRequestHeaders } from '@tanstack/react-start/server';

export type ConsentLoadResult = {
  /** Client display name, or client_id if it registered without one. */
  clientName: string;
  requestedScopes: Scope[];
  /** Raw scope string, passed back to the plugin's accept endpoint verbatim. */
  scopeString: string;
  memberships: OrgMembership[];
  activeOrgId: string | null;
  redirectOrigin: string;
};

function originOf(rawUrl: string | null): string {
  if (!rawUrl) return '';
  try {
    return new URL(rawUrl).origin;
  } catch {
    return rawUrl;
  }
}

export const loadOAuthConsentPage = createServerFn({ method: 'POST' })
  .inputValidator((data: { search: string }) => data)
  .handler(async ({ data }): Promise<ConsentLoadResult> => {
    const headers = getRequestHeaders();
    const session = await auth.api.getSession({ headers });
    if (!session) {
      throw new Error('Unauthorized');
    }

    const params = new URLSearchParams(data.search.startsWith('?') ? data.search.slice(1) : data.search);
    const clientId = params.get('client_id');
    if (!clientId) {
      throw new Error('Missing client_id');
    }

    const client = await getOAuthClientById(clientId);
    if (!client) {
      throw new Error('Unknown OAuth client');
    }

    const scopeString = params.get('scope') ?? '';
    const requestedScopes = parseScopeString(scopeString);
    const memberships = await listOrgsForUser(session.user.id);

    return {
      clientName: client.name ?? client.clientId,
      requestedScopes,
      scopeString,
      memberships,
      activeOrgId: session.session.activeOrganizationId ?? null,
      redirectOrigin: originOf(params.get('redirect_uri')),
    };
  });

export const approveOAuthConsent = createServerFn({ method: 'POST' })
  .inputValidator((data: { scope: string; oauthQuery: string }) => data)
  .handler(async ({ data }): Promise<ConsentRedirect> => {
    // Org comes from the session's active org (set via setActive); approveConsent resolves it.
    const headers = getRequestHeaders();
    return approveConsent({ oauthQuery: data.oauthQuery, scope: data.scope, headers });
  });

export const rejectOAuthConsent = createServerFn({ method: 'POST' })
  .inputValidator((data: { scope: string; oauthQuery: string }) => data)
  .handler(async ({ data }): Promise<ConsentRedirect> => {
    const headers = getRequestHeaders();
    return rejectConsent({
      scope: data.scope,
      oauthQuery: data.oauthQuery,
      headers,
    });
  });
